import React, { useState, useEffect } from 'react';
import { useCompanies } from '../../hooks/useCompanies';

const ContactForm = ({ contact = null, onSubmit, onCancel, loading = false }) => {
  const { companies } = useCompanies();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    companyId: '',
    title: '',
    status: 'Lead',
    notes: '',
    tags: []
  });
  const [errors, setErrors] = useState({});

  // Populate form if editing existing contact
  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || '',
        email: contact.email || '',
        phone: contact.phone || '',
        companyId: contact.companyId || '',
        title: contact.title || '',
        status: contact.status || 'Lead',
        notes: contact.notes || '',
        tags: contact.tags || []
      });
    }
  }, [contact]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
      {/* Name Field */}
      <div className="flex flex-col gap-2">
        <label className="text-slate-700 dark:text-white text-sm font-medium" htmlFor="name">
          Full Name *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className="w-full rounded-lg bg-gray-50 dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#92adc9] focus:ring-2 focus:ring-primary focus:border-transparent p-3 text-base transition-all"
          placeholder="John Doe"
        />
        {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
      </div>

      {/* Email Field */}
      <div className="flex flex-col gap-2">
        <label className="text-slate-700 dark:text-white text-sm font-medium" htmlFor="email">
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className="w-full rounded-lg bg-gray-50 dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#92adc9] focus:ring-2 focus:ring-primary focus:border-transparent p-3 text-base transition-all"
          placeholder="john@example.com"
        />
        {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
      </div>

      {/* Phone Field */}
      <div className="flex flex-col gap-2">
        <label className="text-slate-700 dark:text-white text-sm font-medium" htmlFor="phone">
          Phone
        </label>
        <input
          type="tel"
          id="phone"
          name="phone"
          value={formData.phone}
          onChange={handleChange}
          className="w-full rounded-lg bg-gray-50 dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#92adc9] focus:ring-2 focus:ring-primary focus:border-transparent p-3 text-base transition-all"
          placeholder="+39 123 456 7890"
        />
      </div>

      {/* Company Field - Dropdown */}
      <div className="flex flex-col gap-2">
        <label className="text-slate-700 dark:text-white text-sm font-medium" htmlFor="companyId">
          Company
        </label>
        <select
          id="companyId"
          name="companyId"
          value={formData.companyId}
          onChange={handleChange}
          className="w-full rounded-lg bg-gray-50 dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent p-3 text-base transition-all"
        >
          <option value="">-- No Company --</option>
          {companies.map((company) => (
            <option key={company.id} value={company.id}>
              {company.displayName || company.name}
            </option>
          ))}
        </select>
      </div>

      {/* Title Field */}
      <div className="flex flex-col gap-2">
        <label className="text-slate-700 dark:text-white text-sm font-medium" htmlFor="title">
          Job Title
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className="w-full rounded-lg bg-gray-50 dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#92adc9] focus:ring-2 focus:ring-primary focus:border-transparent p-3 text-base transition-all"
          placeholder="CEO, Developer, Manager..."
        />
      </div>

      {/* Status Field */}
      <div className="flex flex-col gap-2">
        <label className="text-slate-700 dark:text-white text-sm font-medium" htmlFor="status">
          Status
        </label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          className="w-full rounded-lg bg-gray-50 dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] text-slate-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent p-3 text-base transition-all"
        >
          <option value="Lead">Lead</option>
          <option value="Prospect">Prospect</option>
          <option value="Customer">Customer</option>
        </select>
      </div>

      {/* Notes Field */}
      <div className="flex flex-col gap-2">
        <label className="text-slate-700 dark:text-white text-sm font-medium" htmlFor="notes">
          Notes
        </label>
        <textarea
          id="notes"
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          rows={4}
          className="w-full rounded-lg bg-gray-50 dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-[#92adc9] focus:ring-2 focus:ring-primary focus:border-transparent p-3 text-base transition-all resize-none"
          placeholder="Add any additional notes..."
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 justify-end pt-4 border-t border-gray-200 dark:border-[#233648]">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-white bg-white dark:bg-[#192633] border border-gray-300 dark:border-[#324d67] rounded-lg hover:bg-gray-50 dark:hover:bg-[#233648] transition-colors disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-blue-600 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? (
            <>
              <span className="material-symbols-outlined text-[18px] animate-spin">refresh</span>
              <span>Saving...</span>
            </>
          ) : (
            <span>{contact ? 'Update Contact' : 'Create Contact'}</span>
          )}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
